/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';

import renderUnsupportedMediaDialog from '../../components/UnsupportedMediaDialog/renderUnsupportedMediaDialog';
import addAssetProductLinks from '../actions/assets/addAssetProductLinks';
import TYPES from '../action-types';
import getAggregatedData from '../../helpers/assets/getAggregatedData';
import { fetchAssets } from './fetchAssets';
import { fetchListingAssets } from './fetchListingAssets';
import { fetchVariantAssets } from './fetchVariantAssets';
import { addAssets } from './addAssets';
import { updateListingVariantInState } from './helpers/channelHelpers';
import { findListingEntity } from './helpers/entityReadHelpers';
import { upsertListing, upsertListingVariant } from './helpers/entityAdapter';
import { setActiveTabByProductChannel } from './helpers/uiAdapter';
import { resolveListingVariantFetchParamsByListingVariant } from './helpers/resolveListingVariantFetchParams';
import {
  buildListingAttachStoreLinks,
  buildListingAttachStoreLinksFromResponse,
  buildMasterVariantLink,
} from '../helpers/assets/buildAttachStoreLinks';
import { registerPendingPublishAttachLinks } from '../helpers/assets/pendingPublishAttachLinks';

const isListingLinksComplete = (listing, { product, productVariants }) => {
  if (listing.listingGallery && !product) return false;

  const builtVariantIds = new Set((productVariants || []).map(({ variantId }) => String(variantId)));
  return (listing.listingVariantIds || []).every((variantId) => builtVariantIds.has(String(variantId)));
};

const dispatchListingAttachStoreLinks = ({
  dispatch,
  getState,
  masterProduct,
  listing,
  assetIds,
  variantAssetIds,
  isDraft,
  data,
  trackPending,
}) => {
  const state = getState();
  const productsState = state.products;
  const salesChannels = state.user?.team?.salesChannels || [];
  let { product, productVariants } = buildListingAttachStoreLinks({
    masterProduct,
    listing,
    productsState,
    isDraft,
    salesChannels,
  });

  if (isDraft
    && !isListingLinksComplete(listing, { product, productVariants })
    && (data?.updatedListing || data?.updatedVariants?.length)) {
    const responseLinks = buildListingAttachStoreLinksFromResponse({
      listing,
      masterProduct,
      updatedListing: data.updatedListing,
      updatedVariants: data.updatedVariants,
      isDraft: true,
      salesChannels,
    });

    if (!product && responseLinks.product) {
      product = responseLinks.product;
    }

    const builtVariantIds = new Set(productVariants.map(({ variantId }) => String(variantId)));
    const missingResponseVariants = responseLinks.productVariants.filter(
      ({ variantId }) => !builtVariantIds.has(String(variantId)),
    );
    if (missingResponseVariants.length) {
      productVariants = [...productVariants, ...missingResponseVariants];
    }
  }

  const linksIncomplete = !isListingLinksComplete(listing, { product, productVariants });

  if (product) {
    dispatch(addAssetProductLinks({
      assetIds: listing.listingGallery ? assetIds : variantAssetIds,
      product,
    }));
  }

  if (productVariants.length) {
    dispatch(addAssetProductLinks({
      assetIds: variantAssetIds,
      productVariants,
    }));
  }

  if (trackPending) {
    if (product) {
      registerPendingPublishAttachLinks({
        assetIds: listing.listingGallery ? assetIds : variantAssetIds,
        product,
      });
    }
    if (productVariants.length) {
      registerPendingPublishAttachLinks({
        assetIds: variantAssetIds,
        productVariants,
      });
    }
  }

  return linksIncomplete;
};

const refreshSelectedAssetsAggregatedData = async (dispatch, getState, assetIds) => {
  const { assets: { selectedItems, selectedAssetsAggregatedData }, user } = getState();
  const selectedAssetIds = assetIds.filter((id) => selectedItems.includes(id));
  if (!selectedAssetIds.length) return;

  const aggregatedFromApi = await getAggregatedData(selectedAssetIds, user);
  if (!Object.keys(aggregatedFromApi).length) return;

  dispatch({
    type: TYPES.ASSETS.CREATE_COLLECTION.COMPLETE,
    payload: {
      aggregatedData: {
        ...selectedAssetsAggregatedData,
        ...aggregatedFromApi,
      },
    },
  });
};

const collectListingSkipped = (results) => {
  const codes = {};
  const failedIds = [];

  results.forEach(({ skipped, assetsWithReason }) => {
    const items = assetsWithReason || skipped || [];
    items.forEach(({ _id, reason, name }) => {
      failedIds.push(_id);
      codes[reason] = codes[reason] ? [...codes[reason], name] : [name];
    });
  });

  return { failedIds, codes };
};

export const attachAssetsToTargets = createAsyncThunk(
  'products/attachAssetsToTargets',
  async ({
    assetIds,
    masterProductId,
    targets,
  }, {
    dispatch,
    getState,
    extra: {
      sdk, Logger, utils, showErrorDialog, localization: l10n, Toast,
    },
  }) => {
    try {
      const listingResults = [];
      const asyncAssetIds = new Set();
      let processedCount = 0;
      let listingLinksIncomplete = false;
      const masterProduct = getState().products?.items?.find(({ _id }) => _id === masterProductId);

      if (targets.masterGallery) {
        await dispatch(addAssets({
          assetIds,
          product: masterProduct || { _id: masterProductId },
        })).unwrap();
        processedCount += assetIds.length;
      }

      if (targets.masterVariantIds?.length) {
        const variantTargets = targets.masterVariantIds.map((variantId) => ({
          type: 'masterVariant',
          productId: masterProductId,
          variantId,
        }));
        await sdk.assets.attachToTargets(assetIds, variantTargets);
        await Promise.all(
          targets.masterVariantIds.map((variantId) => dispatch(fetchVariantAssets({
            variantId,
            productId: masterProductId,
          }))),
        );
        processedCount += assetIds.length * targets.masterVariantIds.length;

        const variantLinks = targets.masterVariantIds
          .map((variantId) => buildMasterVariantLink(masterProduct, variantId))
          .filter(Boolean);

        if (variantLinks.length) {
          dispatch(addAssetProductLinks({
            assetIds,
            productVariants: variantLinks,
          }));
        }
      }

      if (targets.listings?.length) {
        await Promise.all(targets.listings.map(async (listing) => {
          if (!listing.listingGallery && !listing.listingVariantIds?.length) return;

          const listingVariantIds = listing.listingVariantIds || [];
          const variantAssetIds = listingVariantIds.length ? [assetIds[0]] : assetIds;

          const { data } = await sdk.assets.attachListing(
            listing.listingId,
            assetIds,
            {
              mode: listing.mode || 'draft',
              variantIds: listingVariantIds,
              listingGallery: listing.listingGallery,
            },
          );

          listingResults.push({
            ...data,
            channelId: listing.channelId,
          });

          const isDraftAttach = (listing.mode || 'draft') === 'draft';
          const isSyncAttach = Boolean(data.attachedCount) && !data.assetsToProcessCount;
          const isPublishAsync = !isDraftAttach && Boolean(data.assetsToProcessCount);

          if (isDraftAttach && isSyncAttach) {
            const linksIncomplete = dispatchListingAttachStoreLinks({
              dispatch,
              getState,
              masterProduct,
              listing,
              assetIds,
              variantAssetIds,
              isDraft: true,
              data,
              trackPending: false,
            });
            if (linksIncomplete) {
              listingLinksIncomplete = true;
            }
          } else if (isPublishAsync) {
            dispatchListingAttachStoreLinks({
              dispatch,
              getState,
              masterProduct,
              listing,
              assetIds,
              variantAssetIds,
              isDraft: false,
              data,
              trackPending: true,
            });
          }

          if (data.assetsToProcessCount) {
            processedCount += data.assetsToProcessCount;
            assetIds.forEach((id) => {
              asyncAssetIds.add(id);
            });
          } else if (data.attachedCount) {
            processedCount += data.attachedCount;
          }
        }));
      }

      const { failedIds, codes } = collectListingSkipped(listingResults);
      if (Object.keys(codes).length) {
        renderUnsupportedMediaDialog({
          codes,
          allowedAssetCount: processedCount,
          allAssetCount: assetIds.length,
        });
      }

      if (asyncAssetIds.size > 0) {
        Toast(l10n.TOAST.ATTACHING_ASSETS_TO_PRODUCT_SENT);
      }

      await dispatch(fetchAssets({ productId: masterProductId }));

      if (targets.listings?.length) {
        const productsState = getState().products;

        await Promise.all(targets.listings.map(async (listing) => {
          await dispatch(fetchListingAssets({
            masterProductId,
            channelId: listing.channelId,
            listingProductId: listing.listingId,
          }));

          if (!listing.listingVariantIds?.length) return;

          await Promise.all(listing.listingVariantIds.map(async (listingVariantId) => {
            const params = resolveListingVariantFetchParamsByListingVariant(
              productsState,
              listing.listingId,
              listingVariantId,
            );
            if (!params) return;

            await dispatch(fetchVariantAssets({
              productId: masterProductId,
              variantId: params.linkVariantId,
              channelId: params.channelId,
              masterVariantId: params.masterVariantId,
            }));
          }));
        }));
      }

      if (asyncAssetIds.size === 0 && listingLinksIncomplete) {
        await refreshSelectedAssetsAggregatedData(dispatch, getState, assetIds);
      }

      return {
        masterProductId,
        assetIds,
        targets,
        asyncAssetIds: [...asyncAssetIds],
        listingDraftUpdates: listingResults
          .filter(({ updatedListing, updatedVariants }) => updatedListing || updatedVariants?.length)
          .map(({ channelId, updatedListing, updatedVariants }) => ({
            channelId,
            updatedListing,
            updatedVariants,
          })),
      };
    } catch (error) {
      const errorMessage = utils.getDataFromResponceError(error, 'msg') || 'Can not add assets to product';
      showErrorDialog(errorMessage);
      Logger.error(new Error('Error attach assets to targets'), { error }, [
        'AttachAssetsToTargetsFailed',
        { errorMessage: errorMessage || 'NoMessage' },
      ]);
      throw error;
    }
  },
);

export const reducer = (builder) => {
  builder
    .addCase(attachAssetsToTargets.pending, () => { /* noop */ })
    .addCase(attachAssetsToTargets.fulfilled, (state, { payload }) => {
      const { masterProductId, listingDraftUpdates } = payload;
      if (!listingDraftUpdates?.length) return;

      listingDraftUpdates.forEach(({ channelId, updatedListing, updatedVariants }) => {
        if (updatedListing) {
          const existingListing = findListingEntity(state, { masterProductId, channelId });
          setActiveTabByProductChannel(state, masterProductId, channelId, updatedListing.listingStatus);
          upsertListing(state, {
            ...(existingListing || {}),
            ...updatedListing,
            masterProductId,
          });
        }

        (updatedVariants || []).forEach((variant) => {
          updateListingVariantInState(state, { masterProductId, channelId, data: variant });
          upsertListingVariant(state, {
            ...(state.entities?.listingVariants?.[variant._id] || {}),
            ...variant,
          });
        });
      });
    })
    .addCase(attachAssetsToTargets.rejected, (state, { error }) => {
      state.error = error;
    });
};
